'use client';

import { Input, Text, Flex, Icon } from '@/_components';
import {
  type FC,
  type ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useContext,
} from 'react';
import styles from './OtherEntitySelector.module.css';
import { OtherEntitySelectorProps } from './OtherEntitySelector.types';
import { useRouter } from 'next/navigation';
import { getClasses } from '@/_utils/classnames';
import { Company } from '@/_types/models/company';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { useDebounce } from '@/_hooks/useDebounce';
import { DEFAULT_V2_ENTITY_FILTER } from '@/_constants/entities';
import { getAdminEntities } from '@/_api/v2/admin-entities';

const RELATED_COMPANIES_LIMIT = 10 as const;
const COMPANY_SEARCH_DEBOUNCE = 160 as const;

export const OtherEntitySelector: FC<OtherEntitySelectorProps> = ({
  company,
  relatedCompanies,
  isVisible,
}) => {
  const entitySelectorRef = useRef<HTMLDivElement | null>(null);
  const { push } = useRouter();
  const filterRef = useRef<HTMLInputElement | null>(null);
  const { addToast } = useContext(ToastContext);
  const { token, isAdvisor } = useContext(UserContext);

  const [filter, setFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [companies, setCompanies] = useState<Array<Company>>([]);

  const debouncedSearchValue = useDebounce(filter, COMPANY_SEARCH_DEBOUNCE);

  const onFilterChange = (event: ChangeEvent<HTMLInputElement>) =>
    setFilter(event.target.value);

  const filteredCompanies: Array<Company> = useMemo(
    () =>
      !!filter
        ? companies
        : relatedCompanies?.slice(0, RELATED_COMPANIES_LIMIT),
    [filter, relatedCompanies, companies],
  );

  const otherEntitySelectorClasses: string = getClasses(
    styles.otherEntitySelector,
    'elevation-md',
    {
      [styles.otherEntitySelectorVisible]: isVisible,
    },
  );

  const onResetFilter = () => setFilter('');

  const onCompanyMore = () => push('/admin');

  const fetchCompanies = useCallback(
    async (search: string = '') => {
      if (!token) return;

      try {
        setIsLoading(true);
        const response = await getAdminEntities({
          ...DEFAULT_V2_ENTITY_FILTER,
          token,
          rows_per_page: RELATED_COMPANIES_LIMIT,
          descending: true,
          page: 0,
          search_term: search || '',
        });
        setCompanies(response);
      } catch (error) {
        addToast('error', 'Failed to fetch companies', { error });
      } finally {
        setIsLoading(false);
      }
    },
    [addToast, token],
  );

  useEffect(() => {
    if (!token) return;
    if (!isAdvisor) return;

    fetchCompanies(debouncedSearchValue);
  }, [fetchCompanies, debouncedSearchValue, isAdvisor, addToast, token]);

  useEffect(() => {
    if (isVisible && filterRef?.current) {
      filterRef.current.focus();
    }
  }, [isVisible]);

  return (
    <Flex
      isColumn={true}
      className={otherEntitySelectorClasses}
      gap={4}
      ref={entitySelectorRef}
    >
      <div className={styles.otherEntity__filter}>
        <Input
          value={filter}
          ref={filterRef}
          placeholder="Filter"
          onChange={onFilterChange}
          iconRight={!!filter ? 'x' : undefined}
          onRightClick={onResetFilter}
          autoFocus={true}
          autoComplete="off"
          label="Search"
          isLoading={isLoading}
          isLabelHidden={true}
        />
      </div>
      {filteredCompanies?.map((item, key) => {
        const onCompanySelect = () => {
          const isSame: boolean =
            item?.id?.toString() === company?.id?.toString();

          if (isSame) return;

          setFilter('');

          return push(`/admin/${item.hash_id}`);
        };

        const isIndividual: boolean = item?.entity_type === 'Individual';
        const isActive: boolean = item?.id === company?.id;

        const companyItemClasses: string = getClasses(
          styles.otherEntitySelector__item,
          {
            [styles.otherEntitySelector__itemActive]: isActive,
          },
        );

        return (
          <Flex
            onClick={onCompanySelect}
            key={key}
            gap={4}
            className={companyItemClasses}
          >
            <Icon name={isIndividual ? 'user' : 'briefcase'} size={16} />
            <Text
              fontWeight={isActive ? 'medium' : undefined}
              type="small"
              color={isActive ? 'accent' : undefined}
            >
              {item.name}
            </Text>
          </Flex>
        );
      })}
      {!filter && (
        <Flex
          onClick={onCompanyMore}
          gap={4}
          className={styles.otherEntitySelector__item}
        >
          <Icon name="arrow-left" size={16} />
          <Text fontWeight="medium" type="small">
            All Entities
          </Text>
        </Flex>
      )}
    </Flex>
  );
};
